import styles from './add-to-channel.module.css'

import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { LuLayoutGrid } from '@react-icons/all-files/lu/LuLayoutGrid'
import { LuSearch } from '@react-icons/all-files/lu/LuSearch'
import { LuX } from '@react-icons/all-files/lu/LuX'
import * as React from 'react'
import { connect } from 'react-redux'

import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import { getGroupsWithNesting } from '@disco/state-management/selectors/groups'
import {
  getChannelAndFoldersAndSubFoldersMatchingSearchTerm,
  getGroupName,
  getSelectedNestedGroupFamily,
} from '@disco/utils/groups'
import { tokenizeSearchText } from '@disco/utils/string-utils'

import type { GroupWithNesting, SelectedNestedGroupFamily } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  IconButton,
  ScrollArea,
  Text,
  TextField,
  Tooltip,
} from 'components/ds'
import HighlightedText from 'components/ui/highlighted-text'

function PillWithTooltip({
  label,
  onRemove,
}: {
  label: string
  onRemove?: () => void
}) {
  const nameRef = React.useRef<HTMLSpanElement>(null)
  const [truncated, setTruncated] = React.useState(false)

  React.useLayoutEffect(() => {
    const el = nameRef.current
    if (el) setTruncated(el.scrollWidth > el.offsetWidth)
  }, [label])

  const badge = (
    <Badge variant="soft" radius="full" size="2">
      <LuLayoutGrid size={12} />
      <span ref={nameRef} className={styles.pillName}>
        {label}
      </span>
      {onRemove && (
        <button
          className={styles.pillRemove}
          aria-label={`Remove from ${label}`}
          onClick={onRemove}
        >
          <LuX size={10} />
        </button>
      )}
    </Badge>
  )

  return truncated ? <Tooltip content={label}>{badge}</Tooltip> : badge
}

type Props = {
  allNestedGroups: GroupWithNesting[]
  groups: GroupWithNesting[]
  onSubmit: (...args: Array<any>) => any
  onClose: (...args: Array<any>) => any
  onRemoveFromChannel?: (group: GroupWithNesting) => void
  source?: Playlist | Track
  type: 'add' | 'copy'
}

type TreeItem = {
  channel: GroupWithNesting
  folders: GroupWithNesting[]
  subFolders: GroupWithNesting[]
}

type OwnState = {
  selectedIds: Set<number>
  expandedIds: Set<number>
  search: string
  treeSnapshot: TreeItem[]
}

class AddToChannelModal extends React.Component<Props, OwnState> {
  state: OwnState = {
    selectedIds: new Set(),
    expandedIds: new Set(),
    search: '',
    treeSnapshot: [],
  }

  _searchRef = React.createRef<HTMLInputElement>()

  componentDidMount() {
    this._searchRef.current?.focus()
    this.setState({ treeSnapshot: this._getAvailableTree() })
  }

  componentDidUpdate(prevProps: Props, prevState: OwnState) {
    // Groups can hydrate after mount (deep-link/refresh), so the snapshot has
    // to follow the prop rather than staying frozen at whatever mount saw.
    if (prevProps.groups !== this.props.groups) {
      this.setState({ treeSnapshot: this._getAvailableTree() })
    }
    if (prevState.search === this.state.search) return
    const lowerSearch = this.state.search.toLowerCase()
    // Cleared search collapses everything the search auto-expanded.
    if (!lowerSearch.trim()) {
      this.setState({ expandedIds: new Set() })
      return
    }
    // Auto-expand channels with matches so results are visible.
    const filtered = this._getFilteredTree(lowerSearch)
    this.setState(prev => {
      const next = new Set(prev.expandedIds)
      filtered.forEach(({ channel }) => next.add(channel.id))
      return { expandedIds: next }
    })
  }

  _getFilteredTree(lowerSearch: string): TreeItem[] {
    const { treeSnapshot } = this.state
    if (!lowerSearch?.trim()) return treeSnapshot

    return treeSnapshot.flatMap(({ channel, folders, subFolders }) => {
      const matches =
        getChannelAndFoldersAndSubFoldersMatchingSearchTerm(
          channel,
          lowerSearch,
        ) ?? []
      // The helper re-derives children off channel.folders, so it loses the
      // permission filtering the snapshot applied; intersect it back.
      const allowedFolderIds = new Set(folders.map(f => f.id))
      const allowedSubFolderIds = new Set(subFolders.map(sf => sf.id))
      return matches.map(match => ({
        ...match,
        folders: match.folders.filter(f => allowedFolderIds.has(f.id)),
        subFolders: match.subFolders.filter(sf =>
          allowedSubFolderIds.has(sf.id),
        ),
      }))
    })
  }

  _onSubmit = () => {
    if (this.state.selectedIds.size === 0) return
    const allAvailable = this.state.treeSnapshot.flatMap(
      ({ channel, folders, subFolders }) => [
        channel,
        ...folders,
        ...subFolders,
      ],
    )
    const selected = allAvailable
      .filter(g => this.state.selectedIds.has(g.id))
      .map(g => ({ value: g.id, label: getGroupName(g) }))
    this.props.onSubmit(selected)
  }

  _isCurrentChannel(group: GroupWithNesting) {
    const { source } = this.props
    if (!source || !source.groups) return false
    return source.groups.some(
      activeGroup => activeGroup && activeGroup === group.url,
    )
  }

  _getPillLabel(channelSelection: SelectedNestedGroupFamily): {
    display: string
    full: string
  } {
    const parent = channelSelection.parent
    const parentName = getGroupName(parent)
    const folder = channelSelection?.folder
    const subFolder = channelSelection?.subFolder

    const displayName: string[] = []
    if (parentName) displayName.push(parentName)
    if (folder?.name) displayName.push(folder.name)
    if (subFolder?.name) displayName.push(subFolder.name)

    const full =
      displayName.length > 0 ? displayName.join(' / ') : getGroupName(parent)
    return { display: full, full }
  }

  _getCurrentChannels(): GroupWithNesting[] {
    return this.props.groups.filter(
      group =>
        group.group_type === CUSTOM_CHANNEL &&
        !group.is_auto_tagged &&
        this._isCurrentChannel(group),
    )
  }

  _getAvailableTree(): {
    channel: GroupWithNesting
    folders: GroupWithNesting[]
    subFolders: GroupWithNesting[]
  }[] {
    const topLevel = this.props.groups.filter(
      group =>
        group.group_type === CUSTOM_CHANNEL &&
        !group.parent &&
        !group.is_auto_tagged &&
        !group.lock_content &&
        group.permissions.content,
    )
    return topLevel.map(channel => {
      const folders = (channel.folders ?? []).filter(
        f => !f.is_auto_tagged && f.permissions.content,
      )
      const subFolders = folders
        .flatMap(f => f.folders ?? [])
        .filter(sf => !sf.is_auto_tagged && sf.permissions.content)
      return { channel, folders, subFolders }
    })
  }

  _toggleExpanded = (id: number) => {
    this.setState(prev => {
      const next = new Set(prev.expandedIds)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { expandedIds: next }
    })
  }

  _toggleChannel = (id: number) => {
    this.setState(prev => {
      const next = new Set(prev.selectedIds)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return { selectedIds: next }
    })
  }

  _clearSearch = () => {
    this.setState({ search: '' })
    this._searchRef.current?.focus()
  }

  _handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only submit on Enter from the search field, so Enter/Space still reach
    // focused rows and controls inside the dialog.
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
      e.preventDefault()
      this._onSubmit()
    }
  }

  render() {
    const { type, onRemoveFromChannel } = this.props
    const { selectedIds, expandedIds, search } = this.state

    const currentChannels = this._getCurrentChannels()
    const lowerSearch = search.toLowerCase()
    const filteredTree = this._getFilteredTree(lowerSearch)

    const searchTokens = tokenizeSearchText(search)

    const selectedCount = selectedIds.size
    const ctaLabel =
      type === 'copy'
        ? selectedCount > 0
          ? `Copy to ${selectedCount} channel${selectedCount > 1 ? 's' : ''}`
          : 'Copy to channel'
        : selectedCount > 0
        ? `Add to ${selectedCount} channel${selectedCount > 1 ? 's' : ''}`
        : 'Add to channel'

    return (
      <Dialog.Root open onOpenChange={open => !open && this.props.onClose()}>
        <Dialog.Content
          onKeyDownCapture={this._handleKeyDownCapture}
          maxWidth="420px"
        >
          <Flex justify="between" align="start" mb="2">
            <Dialog.Title my="0" className={styles.modalTitle}>
              {type === 'copy' ? 'Copy to channel' : 'Manage channels'}
            </Dialog.Title>
            <Dialog.Close>
              <IconButton variant="ghost" color="gray" aria-label="Close">
                <LuX size={16} />
              </IconButton>
            </Dialog.Close>
          </Flex>

          {currentChannels.length > 0 && (
            <Box mb="4">
              <Text
                size="1"
                weight="bold"
                color="gray"
                className={styles.sectionLabel}
              >
                CURRENTLY IN
              </Text>
              <Flex wrap="wrap" gap="2" mt="2">
                {currentChannels.map(group => {
                  const channelSelection = getSelectedNestedGroupFamily(
                    this.props.allNestedGroups,
                    group,
                  )
                  // Null when the group's parent chain doesn't resolve to a
                  // root channel; fall back to the group's own name.
                  const display = channelSelection
                    ? this._getPillLabel(channelSelection)?.display
                    : getGroupName(group)
                  return (
                    <PillWithTooltip
                      key={group.id}
                      label={display}
                      onRemove={
                        onRemoveFromChannel
                          ? () => onRemoveFromChannel(group)
                          : undefined
                      }
                    />
                  )
                })}
              </Flex>
            </Box>
          )}

          <Box mb="3">
            <Text
              size="1"
              weight="bold"
              color="gray"
              className={styles.sectionLabel}
            >
              ADD TO
            </Text>
            <Box mt="2">
              <TextField.Root
                ref={this._searchRef}
                placeholder="Search channels..."
                value={search}
                onChange={e => this.setState({ search: e.target.value })}
              >
                <TextField.Slot>
                  <LuSearch size={14} />
                </TextField.Slot>
                {search !== '' && (
                  <TextField.Slot
                    side="right"
                    tabIndex={-1}
                    className={styles.clearSlot}
                  >
                    <IconButton
                      type="button"
                      size="1"
                      tabIndex={-1}
                      variant="ghost"
                      color="gray"
                      aria-label="Clear search"
                      onClick={this._clearSearch}
                    >
                      <LuX size={14} />
                    </IconButton>
                  </TextField.Slot>
                )}
              </TextField.Root>
            </Box>
          </Box>

          <ScrollArea scrollbars="vertical" className={styles.channelList}>
            <Flex direction="column" pr="3">
              {filteredTree.length === 0 ? (
                <Text size="2" color="gray" align="center">
                  No channels found
                </Text>
              ) : (
                filteredTree.map(item => {
                  const { channel, folders, subFolders } = item
                  const id = channel.id
                  const isCurrentIn = this._isCurrentChannel(channel)
                  const checked = isCurrentIn || selectedIds.has(id)
                  const isExpanded =
                    expandedIds.has(id) ||
                    (folders ?? []).some(
                      f => f.parent != null && expandedIds.has(f.parent),
                    )
                  const hasChildren =
                    subFolders?.length > 0 || folders?.length > 0
                  return (
                    <React.Fragment key={id}>
                      <Flex
                        align="center"
                        gap="2"
                        px="2"
                        py="2"
                        className={styles.channelRow}
                        onClick={() => !isCurrentIn && this._toggleChannel(id)}
                      >
                        {hasChildren ? (
                          <button
                            className={`${styles.chevron}${
                              isExpanded ? ` ${styles.chevronOpen}` : ''
                            }`}
                            onClick={e => {
                              e.stopPropagation()
                              this._toggleExpanded(id)
                            }}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            <LuChevronRight size={14} />
                          </button>
                        ) : (
                          <span className={styles.chevronSpacer} />
                        )}
                        <Checkbox
                          checked={checked}
                          disabled={isCurrentIn}
                          onClick={e => e.stopPropagation()}
                          onCheckedChange={() =>
                            !isCurrentIn && this._toggleChannel(id)
                          }
                        />
                        <Text
                          size="2"
                          color={isCurrentIn ? 'gray' : undefined}
                          className={styles.channelName}
                        >
                          <HighlightedText
                            text={getGroupName(channel)}
                            highlights={searchTokens}
                          />
                        </Text>
                      </Flex>
                      {isExpanded &&
                        folders?.length > 0 &&
                        folders?.map(sf => {
                          const sfCurrentIn = this._isCurrentChannel(sf)
                          const sfChecked =
                            sfCurrentIn || selectedIds.has(sf.id)
                          return (
                            <Flex
                              key={sf.id}
                              align="center"
                              gap="2"
                              pr="2"
                              py="2"
                              className={`${styles.channelRow} ${styles.folderRow}`}
                              onClick={() =>
                                !sfCurrentIn && this._toggleChannel(sf.id)
                              }
                            >
                              <Checkbox
                                checked={sfChecked}
                                disabled={sfCurrentIn}
                                onClick={e => e.stopPropagation()}
                                onCheckedChange={() =>
                                  !sfCurrentIn && this._toggleChannel(sf.id)
                                }
                              />
                              <Text
                                size="2"
                                color={sfCurrentIn ? 'gray' : undefined}
                                className={styles.channelName}
                              >
                                <HighlightedText
                                  text={getGroupName(sf)}
                                  highlights={searchTokens}
                                />
                              </Text>
                            </Flex>
                          )
                        })}
                      {isExpanded &&
                        subFolders?.map(sf => {
                          const sfCurrentIn = this._isCurrentChannel(sf)
                          const sfChecked =
                            sfCurrentIn || selectedIds.has(sf.id)
                          return (
                            <Flex
                              key={sf.id}
                              align="center"
                              gap="2"
                              pr="2"
                              py="2"
                              className={`${styles.channelRow} ${styles.subRow}`}
                              onClick={() =>
                                !sfCurrentIn && this._toggleChannel(sf.id)
                              }
                            >
                              <Checkbox
                                checked={sfChecked}
                                disabled={sfCurrentIn}
                                onClick={e => e.stopPropagation()}
                                onCheckedChange={() =>
                                  !sfCurrentIn && this._toggleChannel(sf.id)
                                }
                              />
                              <Text
                                size="2"
                                color={sfCurrentIn ? 'gray' : undefined}
                                className={styles.channelName}
                              >
                                <HighlightedText
                                  text={getGroupName(sf)}
                                  highlights={searchTokens}
                                />
                              </Text>
                            </Flex>
                          )
                        })}
                    </React.Fragment>
                  )
                })
              )}
            </Flex>
          </ScrollArea>

          <Flex gap="2" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button disabled={selectedCount === 0} onClick={this._onSubmit}>
              {ctaLabel}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    )
  }
}

export default connect(state => ({
  allNestedGroups: getGroupsWithNesting(state),
}))(AddToChannelModal) as any
